import { useState } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp, DollarSign, ChevronDown, ChevronUp, Plus, Check, ShieldAlert,
  Layers, Target, Info, RefreshCw, SlidersHorizontal, History, Wallet,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { URGENCY } from "@/lib/constants";

const fade = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

const PRIO_CLS = {
  "QUICK WIN": "text-emerald-700 bg-emerald-50 border-emerald-200",
  "HIGH PRIORITY": "text-rose-700 bg-rose-50 border-rose-200",
  "MEDIUM PRIORITY": "text-amber-700 bg-amber-50 border-amber-200",
  "LONG-TERM": "text-indigo-700 bg-indigo-50 border-indigo-200",
  "LOW PRIORITY": "text-slate-600 bg-slate-100 border-slate-200",
};
const CONF_CLS = {
  High: "text-emerald-700 bg-emerald-50", Medium: "text-amber-700 bg-amber-50", Low: "text-slate-600 bg-slate-100",
};

function money(sym, band) {
  if (!band) return "—";
  const [lo, hi] = band;
  if ((lo || 0) === (hi || 0)) return `${sym}${(lo || 0).toLocaleString()}`;
  return `${sym}${(lo || 0).toLocaleString()}–${sym}${(hi || 0).toLocaleString()}`;
}

function StatusPill({ status }) {
  if (status === "ADMIN_OVERRIDE") {
    return <span data-testid="fin-status-override" className="rounded px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] font-bold uppercase tracking-wide">Admin override</span>;
  }
  return <span data-testid="fin-status-estimated" className="rounded px-2 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-wide">Estimated · Modeled</span>;
}

function Kpi({ label, value, sub, accent = "text-slate-900", tag, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl bg-white/10 backdrop-blur px-4 py-4 border border-white/15">
      <div className={`font-heading text-xl sm:text-2xl font-bold leading-tight ${accent}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-white/70 mt-1">{label}</div>
      {sub && <div className="text-[11px] text-white/60 mt-0.5">{sub}</div>}
      {tag && <div className="mt-1.5 inline-block rounded bg-white/10 border border-white/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white/70">{tag}</div>}
    </div>
  );
}

const PROV_CLS = {
  VERIFIED: "text-emerald-700 bg-emerald-50 border-emerald-200",
  USER_PROVIDED: "text-indigo-700 bg-indigo-50 border-indigo-200",
  CALCULATED: "text-sky-700 bg-sky-50 border-sky-200",
  MODEL_ASSUMPTION: "text-amber-700 bg-amber-50 border-amber-200",
  MODEL_ESTIMATE: "text-slate-600 bg-slate-100 border-slate-200",
};
const PROV_LABEL = {
  VERIFIED: "Verified data", USER_PROVIDED: "User-provided", CALCULATED: "Calculated metric",
  MODEL_ASSUMPTION: "Assumption", MODEL_ESTIMATE: "Model estimate",
};

function OverrideDialog({ issue, sym, open, onOpenChange, onOverride }) {
  const ov = issue.override || {};
  const [low, setLow] = useState(ov?.original_gross_monthly?.[0] ?? issue.gross_monthly?.[0] ?? "");
  const [high, setHigh] = useState(ov?.original_gross_monthly?.[1] ?? issue.gross_monthly?.[1] ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (action) => {
    if ((action === "range" || action === "unavailable") && !reason.trim()) return;
    setBusy(true);
    try {
      await onOverride({ code: issue.code, action, monthly_low: Number(low), monthly_high: Number(high), reason });
      onOpenChange(false);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="fin-override-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Admin override — {issue.problem}</DialogTitle>
          <DialogDescription>
            Adjust the modeled monthly opportunity when you have verified information from the owner.
            The original model value and your reason are preserved in the audit trail. Evidence is never changed.
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-slate-500">
          Current model: <b className="text-slate-700">{money(sym, issue.gross_monthly)}/mo</b>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Monthly low ({sym})</Label>
            <Input data-testid="fin-override-low" type="number" min="0" value={low} onChange={(e) => setLow(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Monthly high ({sym})</Label>
            <Input data-testid="fin-override-high" type="number" min="0" value={high} onChange={(e) => setHigh(e.target.value)} />
          </div>
        </div>
        <div>
          <Label className="text-xs">Reason (required)</Label>
          <Textarea data-testid="fin-override-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Owner supplied verified average customer value of $180." />
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {issue.status === "ADMIN_OVERRIDE" && (
            <Button data-testid="fin-override-clear" variant="outline" disabled={busy} onClick={() => run("clear")}
              className="mr-auto text-slate-600">Clear override</Button>
          )}
          <Button data-testid="fin-override-unavailable" variant="outline" disabled={busy || !reason.trim()} onClick={() => run("unavailable")}>
            Mark unavailable
          </Button>
          <Button data-testid="fin-override-apply" disabled={busy || !reason.trim()} onClick={() => run("range")}
            className="bg-slate-900 hover:bg-slate-800 text-white">
            {busy ? "Saving…" : "Apply override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IssueCard({ issue, sym, onAddToPlan, added, admin, onOverride }) {
  const [showCalc, setShowCalc] = useState(false);
  const [ovOpen, setOvOpen] = useState(false);
  const key = issue.code || issue.problem;
  const isAdded = added[key] || added[issue.problem];
  if (!issue.available) {
    return (
      <div data-testid={`fin-issue-${issue.code}`} className="rounded-xl border border-slate-200 bg-slate-50 p-5">
        <p className="font-semibold text-slate-800">{issue.problem}</p>
        <p className="text-sm text-slate-500 mt-1">{issue.message || "Financial impact cannot be reliably estimated from available data."}</p>
        {issue.admin_override && <span className="mt-2 inline-block text-[10px] font-bold uppercase tracking-wide text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5">Admin override</span>}
      </div>
    );
  }
  return (
    <motion.div {...fade} data-testid={`fin-issue-${issue.code}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{issue.channel_label}</span>
          <p className="font-heading font-semibold text-slate-900 leading-snug">{issue.problem}</p>
        </div>
        <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${PRIO_CLS[issue.priority_category] || PRIO_CLS["LOW PRIORITY"]}`}>{issue.priority_category}</span>
      </div>
      {issue.why_it_matters && <p className="text-sm text-slate-600 mt-2">{issue.why_it_matters}</p>}

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
          <div className="text-[10px] uppercase tracking-wide text-emerald-700/80">Est. annual opportunity</div>
          <div className="font-heading text-lg font-bold text-emerald-800">{money(sym, issue.adjusted_annual)}</div>
          <div className="text-[10px] text-emerald-700/70">{money(sym, issue.gross_annual)} gross · {issue.overlap_pct}% overlap</div>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Est. ROI</div>
          <div className="font-heading text-sm font-semibold text-slate-800 leading-tight">{issue.estimated_roi_note}</div>
        </div>
      </div>

      {issue.status === "ADMIN_OVERRIDE" && issue.override && (
        <div data-testid={`fin-override-note-${issue.code}`} className="mt-3 rounded-lg bg-indigo-50/70 border border-indigo-100 p-3 text-xs">
          <p className="font-semibold text-indigo-800">Admin override applied</p>
          <p className="text-indigo-700/90 mt-0.5">Original model: {money(sym, issue.override.original_gross_monthly)}/mo → adjusted to {money(sym, issue.gross_monthly)}/mo.</p>
          {issue.override.reason && <p className="text-indigo-700/80 mt-0.5">Reason: {issue.override.reason}</p>}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
        {issue.urgency && URGENCY[issue.urgency] && (
          <span data-testid={`fin-urgency-${issue.code}`} className={`rounded px-2 py-0.5 border font-bold uppercase tracking-wide ${URGENCY[issue.urgency].cls}`}>{URGENCY[issue.urgency].label}</span>
        )}
        {issue.impact_type_label && (
          <span className="rounded px-2 py-0.5 bg-slate-100 text-slate-500 font-semibold">{issue.impact_type_label}</span>
        )}
        <StatusPill status={issue.status} />
        <span className={`rounded px-2 py-0.5 font-semibold ${CONF_CLS[issue.confidence]}`}>Confidence: {issue.confidence}</span>
        <span className="rounded px-2 py-0.5 bg-slate-100 text-slate-600">Difficulty: {issue.effort}</span>
        <span className="rounded px-2 py-0.5 bg-slate-100 text-slate-600">~{issue.effort_hours}h</span>
      </div>

      {issue.recommended_action && <p className="text-sm text-slate-700 mt-3"><span className="font-semibold">Recommended fix:</span> {issue.recommended_action}</p>}

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button data-testid={`fin-calc-toggle-${issue.code}`} onClick={() => setShowCalc((v) => !v)}
          className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800">
          {showCalc ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} How was this calculated?
        </button>
        {admin && (
          <button data-testid={`fin-override-btn-${issue.code}`} onClick={() => setOvOpen(true)}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800">
            <SlidersHorizontal className="w-3.5 h-3.5" /> Adjust
          </button>
        )}
        <button data-testid={`fin-add-${issue.code}`} onClick={() => onAddToPlan(issue)} disabled={isAdded}
          className={`ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${isAdded ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
          {isAdded ? <><Check className="w-3.5 h-3.5" /> In Fix Plan</> : <><Plus className="w-3.5 h-3.5" /> Add to Fix Plan</>}
        </button>
      </div>

      {showCalc && (
        <div data-testid={`fin-calc-${issue.code}`} className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1">
          <p><span className="font-semibold">Modeled monthly revenue:</span> {money(sym, issue.calculation?.baseline_monthly_revenue_range)} <span className="text-slate-400">(MODEL ASSUMPTION — category benchmark)</span></p>
          <p><span className="font-semibold">Issue uplift range:</span> {issue.calculation?.uplift_range_pct?.[0]}%–{issue.calculation?.uplift_range_pct?.[1]}% · mode: {issue.calculation?.modeling_mode}</p>
          <p><span className="font-semibold">Formula:</span> {issue.calculation?.formula}</p>
          {issue.evidence && <p><span className="font-semibold">Evidence:</span> {typeof issue.evidence === "string" ? issue.evidence : JSON.stringify(issue.evidence)}</p>}
          <p className="text-slate-400">Gross {money(sym, issue.gross_annual)}/yr → overlap-adjusted {money(sym, issue.adjusted_annual)}/yr.</p>
        </div>
      )}

      {admin && <OverrideDialog issue={issue} sym={sym} open={ovOpen} onOpenChange={setOvOpen} onOverride={onOverride} />}
    </motion.div>
  );
}

export function FinancialImpact({ financial, sym = "$", onAddToPlan, added = {}, admin = false, onRecompute, onOverride, version }) {
  const [recomputing, setRecomputing] = useState(false);
  const [showMethod, setShowMethod] = useState(false);
  if (!financial) return null;

  const AdminBar = admin ? (
    <div data-testid="fin-admin-bar" className="rounded-xl border border-indigo-200 bg-indigo-50/70 p-4 flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2 text-indigo-800">
        <History className="w-4 h-4" />
        <span className="text-sm font-semibold">Admin controls</span>
        {version != null && <span className="text-xs text-indigo-600">· version {version}</span>}
      </div>
      <p className="text-xs text-indigo-700/80 w-full sm:w-auto sm:flex-1">
        Recompute creates a new version; the original computation and evidence are preserved.
      </p>
      <div className="flex items-center gap-2 ml-auto">
        <Button data-testid="fin-recompute-preserved" variant="outline" disabled={recomputing}
          onClick={async () => { setRecomputing(true); try { await onRecompute({ use_current: false }); } finally { setRecomputing(false); } }}
          className="text-xs">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${recomputing ? "animate-spin" : ""}`} /> Recompute
        </Button>
        <Button data-testid="fin-recompute-current" disabled={recomputing}
          onClick={async () => { setRecomputing(true); try { await onRecompute({ use_current: true }); } finally { setRecomputing(false); } }}
          className="text-xs bg-slate-900 hover:bg-slate-800 text-white">
          Recompute with current settings
        </Button>
      </div>
    </div>
  ) : null;

  if (!financial.available) {
    return (
      <div className="space-y-4" data-testid="financial-impact">
        {AdminBar}
        <div data-testid="financial-unavailable" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-1"><ShieldAlert className="w-5 h-5 text-amber-500" /><h3 className="font-heading font-semibold text-slate-900">Estimated Business Opportunity</h3></div>
          <p className="text-sm text-slate-600">{financial.message || "Insufficient data to reliably estimate financial impact."}</p>
          <p className="text-xs text-slate-400 mt-2">{financial.disclaimer}</p>
        </div>
      </div>
    );
  }
  const t = financial.totals || {};
  const p = financial.pricing || {};
  const issues = financial.issues || [];

  return (
    <motion.div {...fade} className="space-y-6" data-testid="financial-impact">
      {AdminBar}
      {/* Client-facing hero */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 text-white p-6 sm:p-8 shadow-lg">
        <div className="flex items-center gap-2 text-white/80 text-xs font-semibold uppercase tracking-[0.16em] mb-1"><TrendingUp className="w-4 h-4" /> Estimated · Modeled</div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">The modeled opportunity in addressing these issues</h2>
        <p className="text-white/70 text-sm mt-1">Modeled from the evidence in this scan using {financial.mode} assumptions. These are estimates of opportunity — not actual losses or guaranteed results.</p>
        {financial.industry_model?.tailored && (
          <div className="mt-3 inline-flex flex-wrap items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs text-white/85" data-testid="fin-industry">
            <span className="font-semibold">Tailored for {financial.industry_model.label}</span>
            <span className="text-white/60">· {financial.industry_model.matters_most}</span>
          </div>
        )}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-3">
          <Kpi testid="fin-kpi-annual" label="Est. annual opportunity" value={money(sym, t.adjusted_annual)} accent="text-emerald-300" tag="Calculated estimate" />
          <Kpi testid="fin-kpi-monthly" label="Est. monthly opportunity" value={money(sym, t.adjusted_monthly)} accent="text-emerald-300" sub="= annual ÷ 12" />
          <Kpi testid="fin-kpi-high" label="High-priority issues" value={t.high_priority_count ?? 0} sub={t.critical_count ? `${t.critical_count} critical` : undefined} />
          <Kpi testid="fin-kpi-highest" label="Highest-value opportunity" value={money(sym, t.highest_value_annual)} sub="single biggest fix" />
          <Kpi testid="fin-kpi-service" label={p.label ? "Suggested service investment" : "Service investment"} value={`${sym}${(p.suggested_value || 0).toLocaleString()}`} sub={p.method === "percentage" ? `${p.fraction_pct ?? Math.round((p.service_fraction || 0) * 100)}% of range midpoint${p.min_applied ? " · min applied" : ""}` : p.method} />
          <Kpi testid="fin-kpi-remaining" label="Est. remaining after investment" value={money(sym, t.remaining_after_investment)} sub={t.remaining_floored ? "low end floored at $0 — see note" : "= opportunity − investment"} />
        </div>
        {t.remaining_floored && (
          <p data-testid="fin-remaining-floor-note" className="mt-3 text-[11px] text-white/60 leading-relaxed flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {(financial.reconciliation?.low_end_floor) || "At the conservative low end the suggested investment can exceed the modeled opportunity; remaining is shown as $0 rather than a negative. This reflects the width of the estimate, not a projected loss."}
            {Array.isArray(t.net_after_investment) && t.net_after_investment[0] < 0 && (
              <span className="text-white/45"> (unfloored low end: −{sym}{Math.abs(t.net_after_investment[0]).toLocaleString()})</span>
            )}
          </p>
        )}
      </div>

      {/* Opportunity math */}
      <div className="grid sm:grid-cols-3 gap-4" data-testid="fin-totals">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1"><Layers className="w-4 h-4" /> Gross opportunity</div>
          <div className="font-heading text-lg font-bold text-slate-800">{money(sym, t.gross_annual)}/yr</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1"><Info className="w-4 h-4" /> Overlap adjustment</div>
          <div className="font-heading text-lg font-bold text-amber-700">− {money(sym, t.overlap_adjustment_annual)}/yr</div>
          <div className="text-[11px] text-slate-400">Prevents double-counting overlapping issues{t.global_overlap_applied ? ` (+${t.global_overlap_applied}% global cap)` : ""}.</div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
          <div className="flex items-center gap-1.5 text-xs text-emerald-700 mb-1"><Target className="w-4 h-4" /> Adjusted opportunity</div>
          <div className="font-heading text-lg font-bold text-emerald-800">{money(sym, t.adjusted_annual)}/yr</div>
        </div>
      </div>

      {/* Pricing note (kept separate from the opportunity) */}
      <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 flex items-start gap-3" data-testid="fin-pricing">
        <DollarSign className="w-5 h-5 text-indigo-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-indigo-900">{p.label}: {sym}{(p.suggested_value || 0).toLocaleString()}</p>
          {p.method === "percentage" && p.basis_value != null && (
            <p className="text-xs text-indigo-700/90 mt-0.5" data-testid="fin-pricing-formula">
              {p.fraction_pct ?? Math.round((p.service_fraction || 0) * 100)}% × {sym}{Number(p.basis_value).toLocaleString()} (midpoint of the {money(sym, t.adjusted_annual)} range)
              {` → rounded to nearest $${p.rounded_to || 50}`}{p.min_applied ? `, raised to the ${sym}${Number(p.min_price).toLocaleString()} minimum` : ""}.
            </p>
          )}
          <p className="text-xs text-indigo-700/80 mt-0.5">{p.note}</p>
          <p className="text-xs text-indigo-700/70 mt-1 flex items-center gap-1.5">
            <Wallet className="w-3.5 h-3.5" /> Remaining opportunity = {money(sym, t.adjusted_annual)} − {sym}{(p.suggested_value || 0).toLocaleString()} = <b>{money(sym, t.remaining_after_investment)}/yr</b>{t.remaining_floored ? " (low end floored at $0)" : ""}
          </p>
        </div>
      </div>

      {/* Issue cards */}
      <div className="grid lg:grid-cols-2 gap-4">
        {issues.map((i) => (
          <IssueCard key={i.code || i.problem} issue={i} sym={sym} onAddToPlan={onAddToPlan} added={added} admin={admin} onOverride={onOverride} />
        ))}
      </div>

      {/* Accessible methodology, reconciliation & provenance */}
      <div className="border-t border-slate-200 pt-4" data-testid="fin-methodology">
        <button data-testid="fin-methodology-toggle" onClick={() => setShowMethod((v) => !v)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900">
          {showMethod ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          How these numbers were calculated (methodology & assumptions)
        </button>
        {showMethod && (
          <div data-testid="fin-methodology-body" className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4 text-xs text-slate-600">
            <div>
              <p className="font-semibold text-slate-700 mb-1">Starting assumption</p>
              <p>
                Modeled monthly revenue: <b>{money(sym, financial.assumptions?.modeled_monthly_revenue_range)}</b>
                <span className="ml-1.5 inline-block rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700">Assumption</span>
              </p>
              <p className="text-slate-500 mt-0.5">{financial.assumptions?.note} Based on the {financial.industry_model?.label} benchmark (avg ticket × monthly customers). No verified traffic or revenue data was used.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-700 mb-1">How every figure reconciles</p>
              <ul className="space-y-1 list-disc pl-4">
                {financial.reconciliation && Object.entries({
                  overlap_identity: null, remaining_identity: null, monthly_definition: null, pricing_basis: null,
                }).map(([k]) => financial.reconciliation[k] ? <li key={k}>{financial.reconciliation[k]}</li> : null)}
              </ul>
              {t.remaining_floored && financial.reconciliation?.low_end_floor && (
                <p className="mt-1.5 text-slate-500"><b>Low-end floor:</b> {financial.reconciliation.low_end_floor}</p>
              )}
            </div>
            <div>
              <p className="font-semibold text-slate-700 mb-1.5">What kind of data each figure is</p>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="fin-provenance-legend">
                {["VERIFIED", "USER_PROVIDED", "CALCULATED", "MODEL_ASSUMPTION", "MODEL_ESTIMATE"].map((k) => (
                  <span key={k} className={`rounded border px-2 py-0.5 text-[10px] font-semibold ${PROV_CLS[k]}`} title={financial.provenance_legend?.[k]}>{PROV_LABEL[k]}</span>
                ))}
              </div>
              <ul className="space-y-0.5">
                <li>Opportunity totals, remaining & suggested investment — <b>Calculated</b> from the assumptions above.</li>
                <li>Monthly revenue baseline & per-issue uplift — <b>Assumptions</b> (industry benchmarks).</li>
                {financial.provenance?.user_provided_inputs_used && <li>An admin override supplied a <b>User-provided</b> figure on at least one issue.</li>}
                <li className="text-slate-500">No figure here is verified against your actual books or presented as guaranteed revenue.</li>
              </ul>
            </div>
            <p className="text-slate-500">{financial.methodology}</p>
          </div>
        )}
        <p className="text-xs text-slate-400 leading-relaxed mt-3" data-testid="fin-disclaimer">{financial.disclaimer}</p>
      </div>
    </motion.div>
  );
}
